import { inject, Injectable, signal } from '@angular/core';
import { ActionCodeSettings, applyActionCode, createUserWithEmailAndPassword, EmailAuthProvider, GoogleAuthProvider, OAuthProvider, linkWithCredential, reload, sendEmailVerification, signInWithEmailAndPassword, signInWithPopup, signOut, updateProfile, User, user } from '@angular/fire/auth';
import { AuthUser, SubscriptionClaims } from '../models/authuser.model';
import { from, Observable, BehaviorSubject, catchError, map, of, race, switchMap, take, timer, filter } from 'rxjs';
import { AuthStaus } from '../models/auth-status.models';
import { UserModel } from '../models/register-user.model';
import { FIREBASE_AUTH } from '../../fireauth-lib.config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private auth = inject(FIREBASE_AUTH); // ✅ Inject Firebase Auth from library
  isLoggedIn = signal(false);
  currentUser = signal<AuthUser | undefined>(undefined);
  fbUser$ = user(this.auth);

  /** When true, the next fbUser$ emission skips the emailVerified gate (OTP-verified registration). */
  private _skipVerificationCheck = false;

  // Add this property to expose the auth state as an Observable
  private authStateSubject = new BehaviorSubject<any>(null);
  public authState$: Observable<any> = this.authStateSubject.asObservable();

  /**
   * Check if a Firebase user has a Google provider linked.
   * Google always verifies email, so users with google.com provider
   * can be treated as email-verified even if emailVerified flag is false
   * (which can happen after linkWithCredential with email/password).
   */
  private _hasGoogleProvider(firebaseUser: User): boolean {
    return firebaseUser.providerData.some(p => p.providerId === 'google.com');
  }

  FIREBASE_ERROR_MESSAGES: Record<string, string> = {
    'email-already-in-use': 'That email is already registered. Please log in or reset your password.',
    'invalid-email':        'Hmm, that email doesn\'t look right. Please check and try again.',
    'weak-password':        'Your password is too weak. Please choose something longer.',
    'user-not-found':       'No account found with that email address.',
    'wrong-password':       'Incorrect password. Please try again or reset your password.',
    'popup-blocked':        'The sign-in popup was blocked by your browser. Please allow popups and try again.',
    'popup-closed-by-user': 'Sign-in was cancelled. Please try again when ready.',
    'account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method. Please log in with your original method.',
    'credential-already-in-use': 'This credential is already associated with another account.',
    'operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
    // …add as many codes as you need
  };

  /**
   * Register a new user with Firebase Auth
   * Note: User metadata (organization, roles, profile) should be created via backend API after registration
   * @param user User registration data
   * @param shouldSendVerificationEmail Whether to send email verification
   * @returns Observable of Firebase User
   */
  register(user: UserModel, shouldSendVerificationEmail: boolean, continueUrl?: string): Observable<User> {
    const registerPromise = async (): Promise<User> => {
      try {
        // Create user in Firebase Auth
        const userCredentials = await createUserWithEmailAndPassword(this.auth, user.email, user.password);
        const authUser = userCredentials.user;

        // Update display name and photo URL in Firebase Auth profile
        await updateProfile(authUser, {
          displayName: user.displayName,
          photoURL: user.photoUrl,
        });

        // Send email verification if requested
        if (shouldSendVerificationEmail && this.auth.currentUser) {
          this.isLoggedIn.set(false);
          this.currentUser.set(undefined);
          const actionSettings: ActionCodeSettings | undefined = continueUrl
            ? { url: continueUrl, handleCodeInApp: false }
            : undefined;
          await sendEmailVerification(this.auth.currentUser, actionSettings);
        }

        // Note: User metadata (firstName, lastName, orgCode, etc.) should be created
        // via backend API call after successful Firebase Auth registration.
        // Components should call the backend registration API with the authUser.uid

        return authUser;

      } catch (error) {
        console.error('Error during registration:', error);
        throw error; // Re-throw to be handled by the Observable's error path
      }
    };

    return from(registerPromise());
  }

  /**
   * Register a new reader whose email has already been verified via OTP.
   * Skips Firebase email-verification and logs the user in immediately by
   * setting the currentUser signal directly, bypassing the emailVerified gate
   * in checkIfUserIsLoggedIn().
   *
   * @param user  Registration data (email, password, displayName)
   * @returns Observable of AuthUser — the user is logged in when this emits
   */
  registerReader(user: UserModel): Observable<AuthUser> {
    const registerPromise = async (): Promise<AuthUser> => {
      try {
        const userCredentials = await createUserWithEmailAndPassword(this.auth, user.email, user.password);
        const firebaseUser = userCredentials.user;

        await updateProfile(firebaseUser, {
          displayName: user.displayName,
          photoURL: user.photoUrl ?? null,
        });

        // Signal checkIfUserIsLoggedIn() to skip the emailVerified gate for the
        // next fbUser$ emission (the one triggered by creating this user).
        this._skipVerificationCheck = true;

        const authUser: AuthUser = {
          userId: firebaseUser.uid,
          email: firebaseUser.email ?? user.email,
          displayName: user.displayName ?? firebaseUser.displayName ?? '',
          photoUrl: firebaseUser.photoURL ?? '',
          emailVerified: true, // trusted: OTP pre-verified the address
          roles: [],
          extUserId: firebaseUser.uid,
          authProvider: 'email',
        };

        this.isLoggedIn.set(true);
        this.currentUser.set(authUser);
        this.authStateSubject.next(authUser);

        return authUser;
      } catch (error) {
        console.error('Error during reader registration:', error);
        this._skipVerificationCheck = false;
        throw error;
      }
    };

    return from(registerPromise());
  }

  /**
   * Update Firebase Auth user profile (displayName and/or photoURL)
   * Can be used to update profile information after registration or anytime during user session
   * @param updates Object containing displayName and/or photoURL to update
   * @returns Observable that completes when update is successful
   */
  updateUserProfile(updates: { displayName?: string; photoURL?: string | null }): Observable<void> {
    return new Observable<void>((observer) => {
      const user = this.auth.currentUser;
      
      if (!user) {
        console.error('Cannot update profile: No user is currently authenticated');
        observer.error(new Error('No authenticated user'));
        return;
      }
      
      updateProfile(user, updates)
        .then(() => {
          console.log('Firebase user profile updated successfully:', updates);
          // Refresh the current user state if needed
          if (this.isLoggedIn()) {
            this.checkIfUserIsLoggedIn();
          }
          observer.next();
          observer.complete();
        })
        .catch((error) => {
          console.error('Failed to update Firebase user profile:', error);
          observer.error(error);
        });
    });
  }

  /**
   * Login with email and password
   * Note: Extended user profile data should be fetched from backend API after successful login
   * @param email User email
   * @param password User password
   * @returns Observable of AuthUser with Firebase Auth data + custom claims
   */
  login(email: string, password: string): Observable<AuthUser> {
    const authPromise = signInWithEmailAndPassword(this.auth, email, password)
      .then(async (userCredentials) => {
        const firebaseUser = userCredentials.user;
        // Accept login if email is verified OR user has a Google provider linked
        // (Google always verifies email; emailVerified can be false after linkWithCredential)
        const isVerified = firebaseUser.emailVerified || this._hasGoogleProvider(firebaseUser);

        if (isVerified) {
          const idTokenResult = await firebaseUser.getIdTokenResult();
          this.isLoggedIn.set(true);
          
          // Extract custom claims set by backend
          const claims = idTokenResult.claims;
          
          // Create AuthUser with Firebase Auth data + properties from custom claims
          const user = {
            userId: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoUrl: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
            orgId: claims['orgId'] as string | undefined,
            orgCode: claims['orgCode'] as string | undefined,
            extUserId: claims['extUserId'] as string | undefined,
            roles: (claims['roles'] as string[]) || [],
            organization: claims['organization'] as string | undefined,
            subscription: (claims['subscription'] as SubscriptionClaims | undefined),
            customClaims: claims
          } as AuthUser;
          
          this.currentUser.set(user);
          this.authStateSubject.next(user);
          return user;
        } else {
          this.isLoggedIn.set(false);
          this.currentUser.set(undefined);
          throw new Error('Email not verified');
        }
      })
    return from(authPromise)
  }

  public isLoggedIn$(): Observable<boolean> {
    return this.fbUser$.pipe(
      map(user => !!user)
    );
  }

  logout(): Observable<void> {
    return from(signOut(this.auth).then(() => {
      this.isLoggedIn.set(false);
      this.currentUser.set(undefined);
      this.authStateSubject.next(null);
    }));
  }

  /**
   * Login for reader accounts whose email was pre-verified via OTP.
   * Identical to login() but skips the emailVerified gate so readers
   * registered without Firebase email-verification can sign in.
   *
   * @param email    User email
   * @param password User password
   * @returns Observable of AuthUser
   */
  loginReader(email: string, password: string): Observable<AuthUser> {
    const authPromise = signInWithEmailAndPassword(this.auth, email, password)
      .then(async (userCredentials) => {
        const firebaseUser = userCredentials.user;
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const claims = idTokenResult.claims;

        const authUser: AuthUser = {
          userId: (claims['extUserId'] as string) || firebaseUser.uid,
          email: firebaseUser.email ?? email,
          displayName: firebaseUser.displayName ?? '',
          photoUrl: firebaseUser.photoURL ?? '',
          emailVerified: true, // trusted: OTP pre-verified during registration
          orgId: claims['orgId'] as string | undefined,
          orgCode: claims['orgCode'] as string | undefined,
          extUserId: firebaseUser.uid,
          roles: (claims['roles'] as string[]) || [],
          organization: claims['organization'] as string | undefined,
          subscription: (claims['subscription'] as SubscriptionClaims | undefined),
          customClaims: claims,
          authProvider: 'email',
        };

        this.isLoggedIn.set(true);
        this.currentUser.set(authUser);
        this.authStateSubject.next(authUser);
        // Allow the next fbUser$ emission (from signIn) to pass through without
        // being blocked by the emailVerified gate.
        this._skipVerificationCheck = true;
        return authUser;
      });
    return from(authPromise);
  }

  public getBearerToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (user) {
      return user.getIdToken();
    }
    return Promise.resolve(null);
  }

  refreshIdToken(): Promise<any> {
    const user = this.auth.currentUser;
    if (user) {
      return user.getIdToken(true);
    }
    return Promise.resolve(null);
  }

  /**
   * Force-refresh the Firebase ID token and update the currentUser signal with
   * the latest custom claims (e.g. extUserId, orgId, roles set by the backend
   * after user creation).  Call this after the backend's createUser() API
   * returns so that the in-memory AuthUser immediately reflects extUserId.
   */
  refreshCurrentUser(): Observable<AuthUser | null> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) return of(null);
    const promise = firebaseUser.getIdTokenResult(true).then(result => {
      const claims = result.claims;
      console.log('[AuthService] refreshCurrentUser — raw subscription claim:', claims['subscription'], '| token issuedAt:', result.issuedAtTime);
      // Preserve userId (MongoDB _id) already set by loginReader() / register flows via
      // currentUser.update(). Never overwrite it here — claims['extUserId'] may not be
      // set yet (backend sets custom claims asynchronously) and falling back to
      // firebaseUser.uid would clobber the MongoDB _id.
      const updated: AuthUser = {
        ...(this.currentUser() ?? {}),
        userId: this.currentUser()?.userId ?? (claims['extUserId'] as string) ?? firebaseUser.uid,
        email: firebaseUser.email ?? '',
        displayName: firebaseUser.displayName ?? '',
        photoUrl: firebaseUser.photoURL ?? '',
        emailVerified: firebaseUser.emailVerified,
        extUserId: claims['extUserId'] as string | undefined,
        orgId: claims['orgId'] as string | undefined,
        orgCode: claims['orgCode'] as string | undefined,
        roles: (claims['roles'] as string[]) || [],
        organization: claims['organization'] as string | undefined,
        subscription: (claims['subscription'] as SubscriptionClaims | undefined),
        customClaims: claims,
      } as AuthUser;
      this.currentUser.set(updated);
      // authState$ drives SubscriptionService/other subscribers — must emit or they never re-sync.
      this.authStateSubject.next(updated);
      return updated;
    });
    return from(promise);
  }

  /**
   * Check if user is logged in and update auth state
   * Note: Only Firebase Auth data is stored in currentUser signal.
   * Extended profile data should be fetched from backend API when needed.
   */
  checkIfUserIsLoggedIn() {
    this.fbUser$.subscribe({
      next: async (authUser) => {
        if (authUser) {
          const emailVerified = authUser.emailVerified;
          
          // Accept users with Google provider even if emailVerified is false
          // (can happen after linkWithCredential with email/password)
          if (!emailVerified && !this._hasGoogleProvider(authUser)) {
            if (this._skipVerificationCheck) {
              // Email was pre-verified via OTP during registerReader() — allow once.
              this._skipVerificationCheck = false;
            } else {
              this.isLoggedIn.set(false);
              this.currentUser.set(undefined);
              return;
            }
          }

          // Get custom claims from ID token (set by backend)
          const idTokenResult = await authUser.getIdTokenResult();
          const claims = idTokenResult.claims;
          
          // Update local state with Firebase Auth data + properties from custom claims
          this.isLoggedIn.set(true);
          this.currentUser.set({
            userId: authUser.uid,
            email: authUser.email ?? '',
            displayName: authUser.displayName ?? '',
            photoUrl: authUser.photoURL ?? '',
            emailVerified: emailVerified,
            orgId: claims['orgId'] as string | undefined,
            orgCode: claims['orgCode'] as string | undefined,
            extUserId: claims['extUserId'] as string | undefined,
            roles: (claims['roles'] as string[]) || [],
            organization: claims['organization'] as string | undefined,
            subscription: (claims['subscription'] as SubscriptionClaims | undefined),
            customClaims: claims
          });
          this.authStateSubject.next(this.currentUser());
        } else {
          this.isLoggedIn.set(false);
          this.currentUser.set(undefined);
        }
      }
    });
  }

  /**
   * Restore auth session for reader apps (e.g. stratum-reader).
   *
   * Unlike checkIfUserIsLoggedIn(), this method does NOT enforce the
   * emailVerified gate. Reader accounts are verified via OTP during
   * registration and never receive a Firebase verification email, so
   * authUser.emailVerified is always false for them. Enforcing the gate
   * would log them out on every page refresh.
   *
   * Google sign-in is still fully supported — Google always verifies email
   * at the provider level.
   *
   * Call this once from the reader AppComponent.ngOnInit(), browser-guarded:
   *   if (isPlatformBrowser(platformId)) this._authService.restoreReaderSession();
   */
  restoreReaderSession(): void {
    this.fbUser$.subscribe({
      next: async (authUser) => {
        if (authUser) {
          const idTokenResult = await authUser.getIdTokenResult();
          const claims = idTokenResult.claims;

          this.isLoggedIn.set(true);
          this.currentUser.set({
            userId: (claims['extUserId'] as string) || authUser.uid,
            email: authUser.email ?? '',
            displayName: authUser.displayName ?? '',
            photoUrl: authUser.photoURL ?? '',
            // Treat as verified: either Firebase-verified, Google provider, or OTP-pre-verified
            emailVerified: authUser.emailVerified || this._hasGoogleProvider(authUser) || true,
            orgId: claims['orgId'] as string | undefined,
            orgCode: claims['orgCode'] as string | undefined,
            extUserId: claims['extUserId'] as string | undefined,
            roles: (claims['roles'] as string[]) || [],
            organization: claims['organization'] as string | undefined,
            subscription: (claims['subscription'] as SubscriptionClaims | undefined),
            customClaims: claims,
          });
          this.authStateSubject.next(this.currentUser());
        } else {
          this.isLoggedIn.set(false);
          this.currentUser.set(undefined);
        }
      }
    });
  }

  /**
   * Resend verification email with rate limiting
   * @returns Observable of AuthStaus indicating success/failure
   */
  resendVerificationEmail(continueUrl?: string): Observable<AuthStaus> {
    const user = this.auth.currentUser;
    if (user && !user.emailVerified) {
      const actionSettings: ActionCodeSettings | undefined = continueUrl
        ? { url: continueUrl, handleCodeInApp: false }
        : undefined;
      const authPromise = sendEmailVerification(this.auth.currentUser!, actionSettings)
        .then(async () => {
          return {
            success: true,
            message: "Verification email sent again. Check your inbox."
          } as AuthStaus
        })
        .catch((error) => {
          return {
            success: false,
            message: error
          } as AuthStaus
        })
      return from(authPromise)
    } else {
      const retStatus: AuthStaus = {
        success: false,
        message: "User not logged in or already verified."
      }
      return from(Promise.resolve({
        success: false,
        message: "User not logged in or already verified."
      } as AuthStaus))
    }
  }

  /**
   * Check email verification status by reloading user
   * Use this to check if user has verified their email
   * @returns Observable of boolean - true if verified, false otherwise
   */
  checkEmailVerification(): Observable<boolean> {
    const user = this.auth.currentUser;
    if (!user) {
      return of(false);
    }

    const checkPromise = user.reload()
      .then(() => {
        const isVerified = this.auth.currentUser?.emailVerified || false;
        
        // If verified, update the current user state
        if (isVerified && this.auth.currentUser) {
          this.checkIfUserIsLoggedIn();
        }
        
        return isVerified;
      })
      .catch((error) => {
        console.error('Error checking email verification:', error);
        return false;
      });

    return from(checkPromise);
  }

  /**
   * Resolves the definitive auth state exactly once, for use by route guards
   * that must work both during in-app navigation and on a hard refresh
   * (where Firebase hasn't restored the session yet).
   *
   * Always emits exactly one value (AuthUser | null) and completes — never
   * completes empty, which would surface as an RxJS EmptyError from Angular's
   * guard resolution (`race()` treats any completion, including an empty one,
   * as a race-deciding notification — so an arm must never be allowed to
   * complete without a value).
   */
  resolveAuthState(claimsTimeoutMs = 3000): Observable<AuthUser | null> {
    // Fast path: auth already settled during in-app navigation.
    if (this.isLoggedIn()) {
      return of(this.currentUser() ?? null);
    }

    // Slow path: hard refresh — Firebase hasn't restored the session yet.
    // fbUser$'s first emission is authoritative (Firebase waits for the
    // persisted session to be restored before firing onAuthStateChanged),
    // so branch on it directly instead of racing an arm that can complete
    // without emitting.
    return this.fbUser$.pipe(
      take(1),
      switchMap(firebaseUser => {
        if (!firebaseUser) {
          return of(null);
        }
        // User IS authenticated — wait for authState$ to receive the
        // AuthUser with custom claims (roles), racing against a safety-valve
        // timeout. Both arms here always emit exactly one value before
        // completing (authState$ is a BehaviorSubject that never completes;
        // timer always fires once), so this inner race cannot complete empty.
        return race(
          this.authState$.pipe(
            filter((u): u is AuthUser => !!u?.roles?.length),
            take(1)
          ),
          timer(claimsTimeoutMs).pipe(map(() => null))
        );
      })
    );
  }

  // If you have a method that checks the current auth state on app initialization,
  // make sure it updates the subject
  initializeAuthState() {
    // Subscribe to Firebase auth state changes. This is the ONLY place that
    // should update authState$ — checkIfUserIsLoggedIn() reads custom claims
    // and emits a proper AuthUser. We do NOT push auth.currentUser directly
    // because it is a raw Firebase User with no custom claims (no orgId, roles,
    // subscription, etc.), which causes resolvers/guards waiting on authState$
    // to receive a false emission and misbehave.
    this.checkIfUserIsLoggedIn();
  }

  getFirebaseErrorCode(error: any) {
    // SDK v9+: error.code is usually "auth/email-already-in-use"
    if (typeof error.code === 'string') {
      const parts = error.code.split('/');
      return parts.length > 1 ? parts[1] : parts[0];
    }
    // Fallback: parse from error.message, e.g. "Firebase: Error (auth/email-already-in-use)."
    if (typeof error.message === 'string') {
      const match = error.message.match(/\(auth\/([^)]+)\)/);
      if (match) return match[1];
    }
    return null;
  }

  getFriendlyErrorMessage(error: any): string {
    const code = this.getFirebaseErrorCode(error) as string;
    if (code && this.FIREBASE_ERROR_MESSAGES[code]) {
      return this.FIREBASE_ERROR_MESSAGES[code];
    }
    // API v2.5.0 normalised error (has success:false + code + message)
    if (
      typeof error === 'object' &&
      error !== null &&
      error.success === false &&
      typeof error.code === 'string' &&
      typeof error.message === 'string'
    ) {
      return error.message as string;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error?.error?.message && typeof error.error.message === 'string') {
      return error.error.message;
    }
    if (error?.message && typeof error.message === 'string') {
      return error.message;
    }
    return 'An unexpected error occurred. Please try again later.';
  }

  /**
   * Sign in with Google using popup.
   * Google accounts are always email-verified by Google.
   * Custom claims won't exist for first-time users — the caller must check
   * if a backend user exists after sign-in.
   * @returns Observable<AuthUser> with Firebase Auth data
   */
  signInWithGoogle(): Observable<AuthUser> {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    const authPromise = signInWithPopup(this.auth, provider)
      .then(async (result) => {
        const firebaseUser = result.user;
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const claims = idTokenResult.claims;

        this.isLoggedIn.set(true);

        const authUser: AuthUser = {
          userId: firebaseUser.uid,
          email: firebaseUser.email ?? '',
          displayName: firebaseUser.displayName ?? '',
          photoUrl: firebaseUser.photoURL ?? '',
          emailVerified: firebaseUser.emailVerified, // Always true for Google
          orgId: claims['orgId'] as string | undefined,
          orgCode: claims['orgCode'] as string | undefined,
          extUserId: claims['extUserId'] as string | undefined,
          roles: (claims['roles'] as string[]) || [],
          organization: claims['organization'] as string | undefined,
          customClaims: claims,
          authProvider: 'google'
        };

        this.currentUser.set(authUser);
        this.authStateSubject.next(authUser);
        return authUser;
      });

    return from(authPromise);
  }

  /**
   * Sign in with Apple using popup.
   * Apple verifies email at the provider level (always true).
   * IMPORTANT: displayName is only returned by Apple on the FIRST sign-in.
   * On subsequent sign-ins firebaseUser.displayName will be null — the caller
   * must capture and pass displayName immediately at sign-in time via router state.
   * Custom claims won't exist for first-time users — caller must check backend.
   * @returns Observable<AuthUser> with Firebase Auth data
   */
  signInWithApple(): Observable<AuthUser> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    const authPromise = signInWithPopup(this.auth, provider)
      .then(async (result) => {
        const firebaseUser = result.user;
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const claims = idTokenResult.claims;

        this.isLoggedIn.set(true);

        const authUser: AuthUser = {
          userId: firebaseUser.uid,
          email: firebaseUser.email ?? '',
          displayName: firebaseUser.displayName ?? '',
          photoUrl: firebaseUser.photoURL ?? '',
          emailVerified: true, // Apple always verifies email at the provider level
          orgId: claims['orgId'] as string | undefined,
          orgCode: claims['orgCode'] as string | undefined,
          extUserId: claims['extUserId'] as string | undefined,
          roles: (claims['roles'] as string[]) || [],
          organization: claims['organization'] as string | undefined,
          customClaims: claims,
          authProvider: 'apple'
        };

        this.currentUser.set(authUser);
        this.authStateSubject.next(authUser);
        return authUser;
      });

    return from(authPromise);
  }

  /**
   * Link email/password credentials to the currently signed-in Google user.
   * This allows the user to log in with either Google or email/password.
   * @param email The user's email address
   * @param password The password to set
   * @returns Observable that completes when linking is successful
   */
  linkEmailPassword(email: string, password: string): Observable<void> {
    return new Observable<void>((observer) => {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        observer.error(new Error('No authenticated user to link credentials to'));
        return;
      }

      const credential = EmailAuthProvider.credential(email, password);
      linkWithCredential(currentUser, credential)
        .then(() => reload(currentUser)) // Reload user to sync emailVerified state after linking
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((error) => {
          console.error('Failed to link email/password:', error);
          observer.error(error);
        });
    });
  }

  /**
   * Rolls back Firebase user creation by deleting the user from Firebase Auth
   * Used when backend registration fails after successful Firebase Auth registration
   * This prevents inconsistent state between Firebase Auth and backend database
   * @returns Observable that completes when rollback is done
   */
  rollbackFirebaseUser(): Observable<void> {
    return new Observable<void>((observer) => {
      console.log('Attempting Firebase user rollback...');
      
      // Validate Firebase Auth instance
      if (!this.auth) {
        console.error('CRITICAL: Cannot access Firebase Auth for rollback');
        observer.error(new Error('Firebase Auth not accessible'));
        return;
      }
      
      // Add a small delay to ensure auth state has propagated
      setTimeout(() => {
        // Get the currently authenticated user (just created during registration)
        const user = this.auth.currentUser;
        
        console.log('Current Firebase user status:', user ? `Found: ${user.uid}` : 'Not found');
        
        if (user) {
          console.log('Deleting Firebase user:', user.uid);
          
          // Delete the user from Firebase Auth
          user.delete()
            .then(() => {
              console.log('Firebase user deleted successfully');
              // Sign out to clear any session remnants
              this.logout().subscribe({
                next: () => {
                  observer.next();
                  observer.complete();
                },
                error: (signoutError: any) => {
                  console.warn('User deleted but signout failed:', signoutError);
                  observer.next(); // Still succeed since user is deleted
                  observer.complete();
                }
              });
            })
            .catch((deleteError: any) => {
              console.error('Failed to delete Firebase user:', deleteError);
              
              // Handle re-authentication required error
              if (deleteError.code === 'auth/requires-recent-login') {
                console.error('CRITICAL: Firebase user deletion requires recent login. User may need manual cleanup.');
              }
              
              observer.error(deleteError);
            });
        } else {
          // No user to delete - this is a problem case
          console.error('No Firebase user found to rollback');
          console.error('This indicates the Firebase Auth session was not maintained after user creation');
          
          // Even though we can't rollback, complete the observable
          // The manual cleanup message will be logged by the calling code
          observer.next();
          observer.complete();
        }
      }, 100); // Small delay to ensure auth state is available
    });
  }
}
